import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { login as apiLogin, getMe, type LoginResponse } from '../api/auth';
import type { User, Client } from '../types';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  role: User['role'];
  reseller_dns?: string;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  impersonating: Client | null;
  originalToken: string | null;

  login: (username: string, password: string) => Promise<LoginResponse | undefined>;
  setAuthFromLogin: (resp: LoginResponse) => Promise<void>;
  setAuth: (token: string, user: AuthUser) => void;
  logout: () => void;
  impersonate: (token: string, client: Client) => void;
  exitImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      impersonating: null,
      originalToken: null,

      login: async (username: string, password: string) => {
        const resp = await apiLogin(username, password);
        if (resp.mfa_required) {
          return resp;
        }
        set({ token: resp.token, user: null, impersonating: null, originalToken: null });
        const me = await getMe();
        set({ user: { id: me.id, username: me.username, email: me.email ?? '', role: me.role } });
        return undefined;
      },

      setAuthFromLogin: async (resp: LoginResponse) => {
        set({ token: resp.token, user: null, impersonating: null, originalToken: null });
        const me = await getMe();
        set({ user: { id: me.id, username: me.username, email: me.email ?? '', role: me.role } });
      },

      setAuth: (token: string, user: AuthUser) => {
        set({ token, user, impersonating: null, originalToken: null });
      },

      logout: () => {
        set({ user: null, token: null, impersonating: null, originalToken: null });
      },

      impersonate: (token: string, client: Client) => {
        set((state) => ({ token, impersonating: client, originalToken: state.token }));
      },

      exitImpersonation: () => {
        set((state) => ({ token: state.originalToken, impersonating: null, originalToken: null }));
      },
    }),
    {
      name: 'xcodec-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        impersonating: state.impersonating,
        originalToken: state.originalToken,
      }),
    }
  )
);
